/**
 * Family Tree Mutation Validator
 *
 * Main validation orchestrator that runs all validation rules
 * and returns a comprehensive validation result.
 *
 * This is the primary API for validating family tree mutations
 * before they are sent to the backend.
 */

import type {
  ValidationResult,
  ValidationViolation,
  FamilyTreeMutation,
  FamilyTreeGraphContext,
  ProductRules,
} from './types';
import { DEFAULT_PRODUCT_RULES } from './types';
import { ALL_VALIDATION_RULES } from './rules';

// ==================== MAIN VALIDATION API ====================

/**
 * Validate a family tree mutation against all rules
 *
 * This is the primary entry point for mutation validation.
 * It runs all validation rules in sequence and returns a complete result.
 *
 * @param mutation - The mutation to validate
 * @param context - Current family tree graph context
 * @param rules - Product rules (optional, defaults to DEFAULT_PRODUCT_RULES)
 * @returns Complete validation result with all violations
 *
 * @example
 * ```typescript
 * const mutation: AddSpouseMutation = {
 *   type: 'add_spouse',
 *   sourcePersonId: 'person-1',
 *   targetPersonId: 'person-2',
 * };
 *
 * const result = validateMutation(mutation, graphContext);
 *
 * if (!result.valid) {
 *   console.error('Validation failed:', result.violations);
 *   result.violations.forEach(violation => {
 *     console.log(`[${violation.severity}] ${violation.message}`);
 *   });
 * }
 * ```
 */
export function validateMutation(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules = DEFAULT_PRODUCT_RULES
): ValidationResult {
  const startTime = performance.now();
  const violations: ValidationViolation[] = [];
  const rulesPassed: string[] = [];

  // Run all validation rules
  for (const rule of ALL_VALIDATION_RULES) {
    try {
      const violation = rule.fn(mutation, context, rules);

      if (violation) {
        violations.push(violation);

        // Stop at first error (warnings don't stop validation)
        if (violation.severity === 'error') {
          break;
        }
      } else {
        rulesPassed.push(rule.name);
      }
    } catch (error) {
      // Catch any unexpected errors during validation
      console.error(`[Validator] Rule "${rule.name}" threw an error:`, error);

      violations.push({
        code: 'UNKNOWN_ERROR',
        severity: 'error',
        message: `Validation rule "${rule.name}" encountered an unexpected error.`,
        details: {
          ruleName: rule.name,
          error: error instanceof Error ? error.message : String(error),
        },
      });

      break; // Stop on unexpected errors
    }
  }

  const endTime = performance.now();

  return {
    valid: violations.filter(v => v.severity === 'error').length === 0,
    violations,
    metadata: {
      rulesPassed,
      rulesChecked: rulesPassed.length + violations.length,
      validationTimestamp: Date.now(),
    },
  };
}

// ==================== BATCH VALIDATION ====================

/**
 * Validate multiple mutations at once
 *
 * Useful for validating a batch of changes before committing them.
 * Returns individual results for each mutation.
 *
 * @param mutations - Array of mutations to validate
 * @param context - Current family tree graph context
 * @param rules - Product rules (optional)
 * @returns Array of validation results (same order as input mutations)
 *
 * @example
 * ```typescript
 * const mutations = [
 *   { type: 'add_spouse', sourcePersonId: 'p1', targetPersonId: 'p2' },
 *   { type: 'add_child', sourcePersonId: 'p1', targetPersonId: 'p3' },
 * ];
 *
 * const results = validateMutationBatch(mutations, graphContext);
 *
 * const allValid = results.every(r => r.valid);
 * ```
 */
export function validateMutationBatch(
  mutations: FamilyTreeMutation[],
  context: FamilyTreeGraphContext,
  rules: ProductRules = DEFAULT_PRODUCT_RULES
): ValidationResult[] {
  return mutations.map(mutation => validateMutation(mutation, context, rules));
}

// ==================== QUICK VALIDATION HELPERS ====================

/**
 * Quick check: Is this mutation valid?
 *
 * Returns a simple boolean without detailed violation information.
 * Use this for fast yes/no checks.
 *
 * @param mutation - The mutation to validate
 * @param context - Current family tree graph context
 * @param rules - Product rules (optional)
 * @returns true if valid, false otherwise
 */
export function isMutationValid(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules = DEFAULT_PRODUCT_RULES
): boolean {
  const result = validateMutation(mutation, context, rules);
  return result.valid;
}

/**
 * Get first error message for a mutation
 *
 * Returns the first error message if validation fails, or null if valid.
 * Useful for showing a single error message to the user.
 *
 * @param mutation - The mutation to validate
 * @param context - Current family tree graph context
 * @param rules - Product rules (optional)
 * @returns Error message or null
 */
export function getFirstErrorMessage(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules = DEFAULT_PRODUCT_RULES
): string | null {
  const result = validateMutation(mutation, context, rules);
  const firstError = result.violations.find(v => v.severity === 'error');
  return firstError ? firstError.message : null;
}

/**
 * Get all error messages for a mutation
 *
 * Returns an array of all error messages (excludes warnings).
 *
 * @param mutation - The mutation to validate
 * @param context - Current family tree graph context
 * @param rules - Product rules (optional)
 * @returns Array of error messages
 */
export function getAllErrorMessages(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules = DEFAULT_PRODUCT_RULES
): string[] {
  const result = validateMutation(mutation, context, rules);
  return result.violations
    .filter(v => v.severity === 'error')
    .map(v => v.message);
}

/**
 * Get all warning messages for a mutation
 *
 * Returns an array of all warning messages (excludes errors).
 *
 * @param mutation - The mutation to validate
 * @param context - Current family tree graph context
 * @param rules - Product rules (optional)
 * @returns Array of warning messages
 */
export function getAllWarningMessages(
  mutation: FamilyTreeMutation,
  context: FamilyTreeGraphContext,
  rules: ProductRules = DEFAULT_PRODUCT_RULES
): string[] {
  const result = validateMutation(mutation, context, rules);
  return result.violations
    .filter(v => v.severity === 'warning')
    .map(v => v.message);
}

// ==================== FORMATTED OUTPUT ====================

/**
 * Format validation result as human-readable string
 *
 * Useful for logging or displaying validation results.
 *
 * @param result - Validation result to format
 * @returns Formatted string
 */
export function formatValidationResult(result: ValidationResult): string {
  if (result.valid) {
    return `✓ Validation passed (${result.metadata?.rulesPassed.length || 0} rules checked)`;
  }

  const errors = result.violations.filter(v => v.severity === 'error');
  const warnings = result.violations.filter(v => v.severity === 'warning');

  let output = '✗ Validation failed:\n';

  if (errors.length > 0) {
    output += '\nErrors:\n';
    errors.forEach((error, idx) => {
      output += `  ${idx + 1}. [${error.code}] ${error.message}\n`;
    });
  }

  if (warnings.length > 0) {
    output += '\nWarnings:\n';
    warnings.forEach((warning, idx) => {
      output += `  ${idx + 1}. [${warning.code}] ${warning.message}\n`;
    });
  }

  return output;
}

/**
 * Format validation result as JSON
 *
 * Useful for API responses or logging.
 *
 * @param result - Validation result to format
 * @returns JSON string
 */
export function formatValidationResultAsJSON(result: ValidationResult): string {
  return JSON.stringify(result, null, 2);
}
